import { area, featureCollection, intersect, polygon } from '@turf/turf';
import { ATTRS, LINESTRING_TYPES, POINT_TYPES, POLYGON_TYPES, TAGS } from './constants';
import { ISO11783Version } from './types';

const lineString2GeoJSON = (isoxmlLineString) => {
  const ring = isoxmlLineString[TAGS.Point].map((point) => [
    parseFloat(point._attributes[ATTRS.Point.PointEast]),
    parseFloat(point._attributes[ATTRS.Point.PointNorth]),
  ]);

  // in ISO11783-10 v3 and earlier linestrings are not supposed to be closed
  if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
    ring.push(ring[0]);
  }

  return ring;
};

export const lineStringABLine2GeoJSON = (isoxmlLineString) => {
  const ring = isoxmlLineString[TAGS.Point].map((point) => [
    parseFloat(point._attributes[ATTRS.Point.PointEast]),
    parseFloat(point._attributes[ATTRS.Point.PointNorth]),
  ]);
  return ring;
};

export const polygonToLineStringXML = (coordinates: any[]) =>
  coordinates.map((ring, idx) => ({
    _attributes: {
      [ATTRS.LineString.LineStringType]:
        idx === 0 ? LINESTRING_TYPES.PolygonExterior : LINESTRING_TYPES.PolygonInterior,
    },
    [TAGS.Point]: ring.map((point) => ({
      _attributes: {
        [ATTRS.Point.PointType]: POINT_TYPES.Other,
        [ATTRS.Point.PointNorth]: point[1],
        [ATTRS.Point.PointEast]: point[0],
      },
    })),
  }));

export const geoJSON2Polygons = (geoJSON, version: ISO11783Version) => {
  const components: any[] =
    geoJSON.type === 'Polygon' ? [geoJSON.coordinates] : geoJSON.coordinates;
  if (version === 3) {
    return [
      {
        _attributes: {
          [ATTRS.Polygon.PolygonType]: POLYGON_TYPES.PartfieldBoundary,
        },
        [TAGS.LineString]: [].concat(...components.map(polygonToLineStringXML)),
      },
    ];
  } else if (version === 4) {
    return components.map((component) => ({
      _attributes: {
        [ATTRS.Polygon.PolygonType]: POLYGON_TYPES.PartfieldBoundary,
      },
      [TAGS.LineString]: polygonToLineStringXML(component),
    }));
  }

  return [];
};

// this algorithm works for both version 3 and version 4 of the ISO11783-10.
// Moreover, it allows polygons with only "inner" rings (formally, it's an invalid geometry)
export const polygons2GeoJSON = (isoxmlPolygons: any[]) => {
  if (!isoxmlPolygons) {
    return null;
  }

  const outerRings = [].concat(
    ...isoxmlPolygons.map((polyXml) =>
      polyXml[TAGS.LineString]
        .filter(
          (ring) =>
            ring._attributes[ATTRS.LineString.LineStringType] === LINESTRING_TYPES.PolygonExterior
        )
        .map(lineString2GeoJSON)
    )
  );

  const innerRings = [].concat(
    ...isoxmlPolygons.map((polyXml) =>
      polyXml[TAGS.LineString]
        .filter(
          (ring) =>
            ring._attributes[ATTRS.LineString.LineStringType] === LINESTRING_TYPES.PolygonInterior
        )
        .map(lineString2GeoJSON)
    )
  );

  let components = null;

  if (outerRings.length === 1) {
    // performance optimization
    components = [[outerRings[0], ...innerRings]];
  } else {
    const outerRingIdxs = innerRings.map((ring) => {
      let maxArea = 0;
      let bestIdx = -1;
      outerRings.forEach((outerRing, idx) => {
        const outerPoly = polygon([outerRing]);
        const innerPoly = polygon([ring]);

        const intersectionRes = intersect(featureCollection([outerPoly, innerPoly]));
        if (intersectionRes) {
          const areaRes = area(intersectionRes);
          if (areaRes > maxArea) {
            maxArea = areaRes;
            bestIdx = idx;
          }
        }
      });
      return bestIdx;
    });

    components = outerRings.map((outerRing, outerIdx) => [
      outerRing,
      ...innerRings.filter((_, innerIdx) => outerRingIdxs[innerIdx] === outerIdx),
    ]);
  }

  if (components.length === 0) {
    return null;
  } else if (components.length === 1) {
    return {
      type: 'Polygon',
      coordinates: components[0],
    };
  } else {
    return {
      type: 'MultiPolygon',
      coordinates: components,
    };
  }
};
